import { AdvancedImage, AdvancedVideo, lazyload, placeholder } from "@cloudinary/react";
import { Cloudinary } from "@cloudinary/url-gen";
import { fill, scale } from "@cloudinary/url-gen/actions/resize";
import { autoGravity } from "@cloudinary/url-gen/qualifiers/gravity";
import { format, quality } from "@cloudinary/url-gen/actions/delivery";
import { auto } from "@cloudinary/url-gen/qualifiers/format";
import { auto as autoQuality } from "@cloudinary/url-gen/qualifiers/quality";

// Initialize Cloudinary instance
// Cloud name is public - it's used in all Cloudinary URLs
const cld = new Cloudinary({
  cloud: {
    cloudName: "dskjtujkp", // HHS Build cloud name
  },
});

interface CloudinaryImageProps {
  publicId: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  /** Use auto-gravity to focus on the most interesting part of the image */
  autoFocus?: boolean;
  /** Resize mode: 'fill' crops to exact dimensions, 'scale' maintains aspect ratio */
  mode?: "fill" | "scale";
  /** Enable lazy loading */
  lazy?: boolean;
  /** Show placeholder while loading */
  showPlaceholder?: boolean;
}

/**
 * CloudinaryImage - Optimized image component using Cloudinary transformations
 * 
 * Benefits:
 * - Automatic format conversion (WebP/AVIF based on browser support)
 * - Automatic quality optimization
 * - Responsive resizing
 * - Lazy loading
 * - Blur-up placeholder
 * 
 * @example
 * <CloudinaryImage 
 *   publicId="hhsbuild-cms/kitchen-renovation"
 *   alt="Kitchen Renovation Project"
 *   width={800}
 *   height={600}
 *   autoFocus
 * />
 */
export function CloudinaryImage({
  publicId,
  alt,
  width,
  height,
  className,
  autoFocus = true,
  mode = "fill",
  lazy = true,
  showPlaceholder = true,
}: CloudinaryImageProps) {
  // Create the image with transformations
  const image = cld.image(publicId);

  // Apply automatic format and quality optimization
  image.delivery(format(auto())).delivery(quality(autoQuality()));

  // Apply resize transformation
  if (width || height) {
    if (mode === "fill") {
      const resizeAction = fill();
      if (width) resizeAction.width(width);
      if (height) resizeAction.height(height);
      if (autoFocus) resizeAction.gravity(autoGravity());
      image.resize(resizeAction);
    } else {
      const resizeAction = scale();
      if (width) resizeAction.width(width);
      if (height) resizeAction.height(height);
      image.resize(resizeAction);
    }
  }

  // Build plugins array based on options
  const plugins = [];
  if (lazy) plugins.push(lazyload());
  if (showPlaceholder) plugins.push(placeholder({ mode: "blur" }));

  return (
    <AdvancedImage
      cldImg={image}
      alt={alt}
      className={className}
      plugins={plugins}
    />
  );
}

interface CloudinaryVideoProps {
  publicId: string;
  width?: number;
  height?: number;
  className?: string;
  /** Auto-play video (muted for browser autoplay policy) */
  autoPlay?: boolean;
  /** Loop video */
  loop?: boolean;
  /** Show video controls */
  controls?: boolean;
  /** Mute video */
  muted?: boolean;
  /** Poster image public ID (optional) */
  poster?: string;
}

/**
 * CloudinaryVideo - Optimized video component using Cloudinary transformations
 * 
 * @example
 * <CloudinaryVideo
 *   publicId="hhsbuild-cms/project-walkthrough"
 *   width={1280}
 *   height={720}
 *   controls
 * />
 */
export function CloudinaryVideo({
  publicId,
  width,
  height,
  className,
  autoPlay = false,
  loop = false,
  controls = true,
  muted = false,
  poster,
}: CloudinaryVideoProps) {
  // Create the video with transformations
  const video = cld.video(publicId);

  // Apply automatic format and quality optimization
  video.delivery(format(auto())).delivery(quality(autoQuality()));

  // Apply resize transformation
  if (width || height) {
    const resizeAction = fill();
    if (width) resizeAction.width(width);
    if (height) resizeAction.height(height);
    video.resize(resizeAction);
  }

  return (
    <AdvancedVideo
      cldVid={video}
      className={className}
      autoPlay={autoPlay}
      loop={loop}
      controls={controls}
      muted={muted || autoPlay} // Must be muted for autoplay
      poster={poster ? cld.image(poster).toURL() : undefined}
      plugins={[lazyload()]}
    />
  );
}

/**
 * Helper to generate a Cloudinary URL directly (for background images, etc.)
 */
export function getCloudinaryUrl(
  publicId: string,
  options?: {
    width?: number;
    height?: number;
    mode?: "fill" | "scale";
  }
): string {
  const image = cld.image(publicId);
  
  image.delivery(format(auto())).delivery(quality(autoQuality()));

  if (options?.width || options?.height) {
    if (options?.mode === "scale") {
      const resizeAction = scale();
      if (options?.width) resizeAction.width(options.width);
      if (options?.height) resizeAction.height(options.height);
      image.resize(resizeAction);
    } else {
      const resizeAction = fill();
      if (options?.width) resizeAction.width(options.width);
      if (options?.height) resizeAction.height(options.height);
      resizeAction.gravity(autoGravity());
      image.resize(resizeAction);
    }
  }

  return image.toURL();
}

export default CloudinaryImage;

